import React, { useEffect, useMemo, useState, useCallback } from "react";
import useLocalStorageAutosave, {
    AutosaveDraftAlert,
    AutosaveSavedIndicator,
} from "../../../hooks/useLocalStorageAutosave";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import Popup from "../../../components/Popup";
import { getApiBaseUrl } from "../../../config/api";
import { getMalawiGrade, getUniversityGrade, computeWeightedTotal } from "../../../utils/gradingUtils";
import { getSchoolIdForUser } from "../../../utils/userContext";

function gradeChipSx(color) {
    const key = color === "success" ? "success.main" : color === "info" ? "info.main" : color === "warning" ? "warning.main" : "error.main";
    return {
        fontWeight: 700,
        bgcolor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.12),
        color: key,
        borderColor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.28),
    };
}

const StudentExamMarks = ({ situation }) => {
    const { currentUser } = useSelector((s) => s.user);
    const { studentID, subjectID, id } = useParams();
    const navigate = useNavigate();

    const schoolId = getSchoolIdForUser(currentUser);
    const isTeacher = currentUser?.role === "Teacher";
    const isReadOnlyView = currentUser?.role === "Admin" || isTeacher;
    const resolvedStudentId = situation === "Subject" ? studentID : id || studentID;
    const fixedSubjectId = situation === "Subject" ? subjectID : null;

    const [pageLoading, setPageLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [student, setStudent] = useState(null);
    const [sclass, setSclass] = useState(null);
    const [subject, setSubject] = useState(null);
    const [subjectsList, setSubjectsList] = useState([]);
    const [selectedSubjectId, setSelectedSubjectId] = useState(fixedSubjectId || "");

    const [marksObtained, setMarksObtained] = useState("");
    const [caScore, setCaScore] = useState("");
    const [examScore, setExamScore] = useState("");
    const [semester, setSemester] = useState("Semester 1");
    const [academicYear, setAcademicYear] = useState("");
    const [yearOfStudy, setYearOfStudy] = useState(1);
    const [creditHours, setCreditHours] = useState(3);

    const [existingPrimaryResult, setExistingPrimaryResult] = useState(null);
    const [existingUniversityGrade, setExistingUniversityGrade] = useState(null);

    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [userHasEdited, setUserHasEdited] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const educationLevel = student?.educationLevel || sclass?.educationLevel || "Secondary";
    const isUniversity = educationLevel === "University";

    const activeSubjectId = fixedSubjectId || selectedSubjectId;

    const classId =
        sclass?._id || sclass?.id || student?.sclassName?._id || student?.sclassName?.id || student?.sclassName;
    const examType = isUniversity ? "university" : String(resolvedStudentId || "primary");
    const autosaveStorageKey =
        activeSubjectId && classId ? `autosave-marks-${activeSubjectId}-${examType}-${classId}` : null;

    const autosaveState = useMemo(
        () => ({
            marksObtained,
            caScore,
            examScore,
            semester,
            academicYear,
            yearOfStudy,
            creditHours,
            selectedSubjectId,
        }),
        [marksObtained, caScore, examScore, semester, academicYear, yearOfStudy, creditHours, selectedSubjectId]
    );

    const {
        draftPrompt,
        restoreDraft,
        discardDraft,
        clearDraft,
        showSavedIndicator,
    } = useLocalStorageAutosave({
        storageKey: autosaveStorageKey,
        state: autosaveState,
        enabled: Boolean(
            !isReadOnlyView && initialLoadDone && !pageLoading && userHasEdited && autosaveStorageKey
        ),
    });

    const markEdited = useCallback(() => setUserHasEdited(true), []);

    const handleRestoreDraft = () => {
        const data = restoreDraft();
        if (!data) return;
        if (data.marksObtained !== undefined) setMarksObtained(data.marksObtained);
        if (data.caScore !== undefined) setCaScore(data.caScore);
        if (data.examScore !== undefined) setExamScore(data.examScore);
        if (data.semester) setSemester(data.semester);
        if (data.academicYear !== undefined) setAcademicYear(data.academicYear);
        if (data.yearOfStudy !== undefined) setYearOfStudy(data.yearOfStudy);
        if (data.creditHours !== undefined) setCreditHours(data.creditHours);
        if (data.selectedSubjectId) setSelectedSubjectId(data.selectedSubjectId);
        setUserHasEdited(true);
    };

    const liveMalawiGrade = useMemo(
        () =>
            getMalawiGrade(
                marksObtained,
                educationLevel,
                sclass?.levelCode,
                sclass?.sclassName
            ),
        [marksObtained, educationLevel, sclass]
    );
    const weightedTotal = useMemo(() => computeWeightedTotal(caScore, examScore), [caScore, examScore]);
    const liveUniversityGrade = useMemo(() => getUniversityGrade(weightedTotal), [weightedTotal]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!resolvedStudentId) return;
            setPageLoading(true);
            try {
                const studentRes = await axios.get(`${getApiBaseUrl()}/Student/${resolvedStudentId}`);
                if (cancelled) return;
                const studentData = studentRes.data;
                if (studentData?.message && !studentData?.name) {
                    throw new Error(studentData.message);
                }
                setStudent(studentData);

                const classId = studentData?.sclassName?._id || studentData?.sclassName?.id || studentData?.sclassName;
                if (classId) {
                    try {
                        const classRes = await axios.get(`${getApiBaseUrl()}/Sclass/${classId}`);
                        if (!cancelled) setSclass(classRes.data);
                    } catch {
                        /* optional */
                    }

                    if (situation !== "Subject") {
                        try {
                            const subjectsRes = await axios.get(`${getApiBaseUrl()}/ClassSubjects/${classId}`);
                            if (!cancelled && Array.isArray(subjectsRes.data)) {
                                setSubjectsList(subjectsRes.data);
                            }
                        } catch {
                            /* optional */
                        }
                    }
                }

                const initialYear = Number(studentData?.yearOfStudy) || 1;
                setYearOfStudy(initialYear);

                if (studentData?.examResult && fixedSubjectId) {
                    const existing = studentData.examResult.find((row) => {
                        const subId = row?.subName?._id || row?.subName?.id || row?.subName;
                        return String(subId) === String(fixedSubjectId);
                    });
                    if (existing) {
                        setExistingPrimaryResult(existing);
                        setMarksObtained(String(existing.marksObtained ?? ""));
                    }
                }

                try {
                    const transcriptRes = await axios.get(`${getApiBaseUrl()}/transcript/${resolvedStudentId}`);
                    if (cancelled) return;
                    const allGrades = (transcriptRes.data?.grades_by_semester || []).flatMap((group) => group.grades || []);
                    const subjectGrades = allGrades.filter((row) => String(row.subjectId) === String(fixedSubjectId || selectedSubjectId || ""));
                    if (subjectGrades.length) {
                        const latest = subjectGrades[subjectGrades.length - 1];
                        setExistingUniversityGrade(latest);
                        setCaScore(String(latest.caScore ?? ""));
                        setExamScore(String(latest.examScore ?? ""));
                        setSemester(latest.semester || "Semester 1");
                        setAcademicYear(latest.academicYear || "");
                        setYearOfStudy(Number(latest.yearOfStudy) || initialYear);
                        setCreditHours(Number(latest.creditHours) || 3);
                    }
                } catch {
                    /* transcript may not be available yet */
                }
            } catch (err) {
                if (!cancelled) {
                    setMessage(err.response?.data?.message || "Could not load student details");
                    setShowPopup(true);
                }
            } finally {
                if (!cancelled) {
                    setPageLoading(false);
                    setInitialLoadDone(true);
                    setUserHasEdited(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [resolvedStudentId, fixedSubjectId, situation, selectedSubjectId]);

    useEffect(() => {
        if (!activeSubjectId) return;
        let cancelled = false;
        const loadSubject = async () => {
            try {
                const result = await axios.get(`${getApiBaseUrl()}/Subject/${activeSubjectId}`);
                if (!cancelled) setSubject(result.data);
            } catch {
                if (!cancelled) setSubject(null);
            }
        };
        loadSubject();
        return () => {
            cancelled = true;
        };
    }, [activeSubjectId]);

    useEffect(() => {
        if (!student?.examResult || !activeSubjectId || isUniversity) return;
        const existing = student.examResult.find((row) => {
            const subId = row?.subName?._id || row?.subName?.id || row?.subName;
            return String(subId) === String(activeSubjectId);
        });
        setExistingPrimaryResult(existing || null);
        if (existing) {
            setMarksObtained(String(existing.marksObtained ?? ""));
        } else {
            setMarksObtained("");
        }
    }, [student, activeSubjectId, isUniversity]);

    useEffect(() => {
        if (!fixedSubjectId && subjectsList.length && !selectedSubjectId) {
            const firstId = subjectsList[0]?._id || subjectsList[0]?.id;
            if (firstId) setSelectedSubjectId(firstId);
        }
    }, [fixedSubjectId, selectedSubjectId, subjectsList]);

    const submitPrimarySecondary = async () => {
        if (isTeacher) {
            setMessage("Teachers must upload test results from Upload test results and submit for admin review.");
            setShowPopup(true);
            return;
        }
        if (!schoolId) {
            setMessage("School information is missing. Please sign in again.");
            setShowPopup(true);
            return;
        }
        if (!activeSubjectId) {
            setMessage("Please select a subject.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/saveExamMarks`, {
                studentId: resolvedStudentId,
                subjectId: activeSubjectId,
                marksObtained: Number(marksObtained),
                schoolId,
            });
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
            } else {
                setExistingPrimaryResult(data);
                clearDraft();
                setUserHasEdited(false);
                setMessage("Marks saved successfully");
            }
            setShowPopup(true);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save marks");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const submitUniversity = async () => {
        if (isTeacher) {
            setMessage("Teachers must upload test results from Upload test results and submit for admin review.");
            setShowPopup(true);
            return;
        }
        if (!schoolId) {
            setMessage("School information is missing. Please sign in again.");
            setShowPopup(true);
            return;
        }
        if (!activeSubjectId) {
            setMessage("Please select a subject.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/saveUniversityGrade`, {
                studentId: resolvedStudentId,
                subjectId: activeSubjectId,
                schoolId,
                academicYear: academicYear.trim(),
                semester,
                yearOfStudy: Number(yearOfStudy) || 1,
                caScore: Number(caScore),
                examScore: Number(examScore),
                creditHours: Number(creditHours) || 3,
            });
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
            } else {
                setExistingUniversityGrade(data);
                clearDraft();
                setUserHasEdited(false);
                setMessage("University grade saved successfully");
            }
            setShowPopup(true);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not save university grade");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isUniversity) {
            submitUniversity();
        } else {
            submitPrimarySecondary();
        }
    };

    const allExamRows = useMemo(() => {
        if (!student?.examResult || !Array.isArray(student.examResult)) return [];
        return student.examResult.filter(
            (r) => r?.subName && r.marksObtained !== undefined && r.marksObtained !== null
        );
    }, [student]);

    if (pageLoading) {
        return (
            <Box sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isReadOnlyView) {
        const backPath = isTeacher
            ? `/Teacher/class/student/${resolvedStudentId}`
            : `/Admin/students/student/${resolvedStudentId}`;

        return (
            <Box
                sx={{
                    minHeight: "100%",
                    py: { xs: 2, sm: 3 },
                    background: (t) =>
                        t.palette.mode === "light"
                            ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                            : alpha(t.palette.common.black, 0.12),
                }}
            >
                <Container maxWidth="md">
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        }}
                    >
                        <Box
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: 2.5,
                                background: (t) =>
                                    `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 50%, ${alpha(
                                        t.palette.primary.light,
                                        0.92
                                    )} 100%)`,
                                color: "common.white",
                            }}
                        >
                            <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                Results (read-only)
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>
                                Exam marks
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                                {student?.name || "Student"} · {sclass?.sclassName || "Class"}
                            </Typography>
                        </Box>
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {isTeacher
                                    ? "Enter marks from Upload test results in the sidebar, then submit for admin approval."
                                    : "Teachers upload test results and submit for approval. Approve under Exams → Marks review, then print end-of-term report cards."}
                            </Alert>
                            {allExamRows.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    Marks
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {allExamRows.map((row, idx) => {
                                                const subLabel =
                                                    row.subName?.subName || row.subName?.name || row.subName || "—";
                                                const grade = getMalawiGrade(
                                                    row.marksObtained,
                                                    educationLevel,
                                                    sclass?.levelCode,
                                                    sclass?.sclassName
                                                );
                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell>{subLabel}</TableCell>
                                                        <TableCell align="right">{row.marksObtained}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={`${grade.grade} · ${grade.label}`}
                                                                variant="outlined"
                                                                sx={gradeChipSx(grade.color)}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    No approved marks on record yet for this student.
                                </Alert>
                            )}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                                {isTeacher ? (
                                    <Button variant="contained" onClick={() => navigate("/Teacher/marks-entry")}>
                                        Record class marks
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="contained"
                                            startIcon={<ArticleOutlinedIcon />}
                                            onClick={() => navigate(`/Admin/students/report-card/${resolvedStudentId}`)}
                                        >
                                            Report card
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FactCheckOutlinedIcon />}
                                            onClick={() => navigate("/Admin/exams/review")}
                                        >
                                            Review marks
                                        </Button>
                                    </>
                                )}
                                <Button variant="text" onClick={() => navigate(backPath)}>
                                    Back to student
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 50%, ${alpha(
                                    t.palette.primary.light,
                                    0.92
                                )} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                            Results
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Exam / Grade Entry
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                            {student?.name || "Student"} · {sclass?.sclassName || student?.sclassName?.sclassName || "Class"} · {educationLevel}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.92 }}>
                            {subject?.subName || "Select subject below"}
                        </Typography>
                    </Box>

                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ p: { xs: 2, sm: 3 }, position: "relative" }}
                    >
                        <AutosaveSavedIndicator show={showSavedIndicator} />
                        <AutosaveDraftAlert
                            draft={draftPrompt}
                            onRestore={handleRestoreDraft}
                            onDiscard={discardDraft}
                        />
                        {isTeacher ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Use <strong>Upload test results</strong> in the sidebar to enter or import marks for your class and send them to the administrator for approval.
                            </Alert>
                        ) : null}
                        {!fixedSubjectId && (
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel id="subject-select-label">Subject</InputLabel>
                                <Select
                                    labelId="subject-select-label"
                                    label="Subject"
                                    value={selectedSubjectId}
                                    onChange={(e) => {
                                        markEdited();
                                        setSelectedSubjectId(e.target.value);
                                    }}
                                >
                                    {subjectsList.map((item) => (
                                        <MenuItem key={item._id || item.id} value={item._id || item.id}>
                                            {item.subName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {!isUniversity ? (
                            <Stack spacing={2.5}>
                                <Alert severity="info">Enter integer marks out of 100. The MSCE grade (1–9) updates automatically.</Alert>

                                {existingPrimaryResult ? (
                                    <Alert severity="success">
                                        Existing saved marks: {existingPrimaryResult.marksObtained}
                                    </Alert>
                                ) : null}

                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Marks Obtained"
                                    value={marksObtained}
                                    onChange={(e) => {
                                        markEdited();
                                        setMarksObtained(e.target.value);
                                    }}
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                />

                                {marksObtained !== "" ? (
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                        <Typography variant="body1" fontWeight={600}>
                                            Live grade:
                                        </Typography>
                                        <Chip
                                            label={`Grade ${liveMalawiGrade.grade}: ${liveMalawiGrade.label}`}
                                            variant="outlined"
                                            sx={gradeChipSx(liveMalawiGrade.color)}
                                        />
                                    </Stack>
                                ) : null}
                            </Stack>
                        ) : (
                            <Stack spacing={2.5}>
                                <Alert severity="info">CA contributes 40% and the final exam contributes 60% of the university grade.</Alert>

                                {existingUniversityGrade ? (
                                    <Alert severity="success">
                                        Existing saved grade: {existingUniversityGrade.gradeLetter} · GPA {existingUniversityGrade.gradePoint} · {existingUniversityGrade.academicYear} {existingUniversityGrade.semester}
                                    </Alert>
                                ) : null}

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="CA Score"
                                            value={caScore}
                                            onChange={(e) => {
                                                markEdited();
                                                setCaScore(e.target.value);
                                            }}
                                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="Exam Score"
                                            value={examScore}
                                            onChange={(e) => {
                                                markEdited();
                                                setExamScore(e.target.value);
                                            }}
                                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel id="semester-label">Semester</InputLabel>
                                            <Select
                                                labelId="semester-label"
                                                label="Semester"
                                                value={semester}
                                                onChange={(e) => {
                                                    markEdited();
                                                    setSemester(e.target.value);
                                                }}
                                            >
                                                <MenuItem value="Semester 1">Semester 1</MenuItem>
                                                <MenuItem value="Semester 2">Semester 2</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Academic Year"
                                            value={academicYear}
                                            onChange={(e) => {
                                                markEdited();
                                                setAcademicYear(e.target.value);
                                            }}
                                            placeholder="e.g. 2024/2025"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            label="Year of Study"
                                            value={yearOfStudy}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Credit Hours"
                                            value={creditHours}
                                            onChange={(e) => {
                                                markEdited();
                                                setCreditHours(e.target.value);
                                            }}
                                            inputProps={{ min: 1, step: 1 }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                {(caScore !== "" || examScore !== "") ? (
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                        <Typography variant="body1" fontWeight={600}>
                                            Weighted Total: {weightedTotal}% → Grade {liveUniversityGrade.letter} → GPA {liveUniversityGrade.point}
                                        </Typography>
                                        <Chip
                                            label={`Grade ${liveUniversityGrade.letter}`}
                                            variant="outlined"
                                            sx={gradeChipSx(liveUniversityGrade.color)}
                                        />
                                    </Stack>
                                ) : null}
                            </Stack>
                        )}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={saving || !activeSubjectId}>
                                {saving ? <CircularProgress size={24} color="inherit" /> : "Save"}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default StudentExamMarks;
